import { useLocation, useNavigate } from "react-router-dom";
import { ContainerCard, EmployeeDetails } from "../../components/ui/cards";
import { ChevronLeft, ChevronRight, FilterIcon, Save, Send } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Checkbox, DatePicker, Select } from "antd";
import { disabledPastDate } from "../../components/common/CommonFunctions";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import { EMPLOYEE_DEPARTMENT, EMPLOYEE_PERSONAL_INFO, EMPLOYEE_SHIFT, INDIVIDUAL_ROSTERING_GET } from "../../constants/api";
import React, { useEffect, useState, useCallback, useRef } from "react";
import ToastMsg from "../../components/common/ToastMsg";
import dayjs from "dayjs";

const RosterAddEdit = () => {
    const { fetch, update } = useApiMethods();
    const navigate = useNavigate();
    const location = useLocation();
    const { roster_id, roster_from_date, roster_to_date, editAccess } = location.state || {};

    // Convert string dates to Day.js objects
    const getInitialDate = (dateString) => {
        return dateString ? dayjs(dateString) : null;
    };

    const [formData, setFormData] = useState({
        groupBy: "employee",
        shift: undefined,
        isMakeRecurring: false,
        rotational_shifts: [],
        initial_rotational_shifts: [],
        is_published: false,
        sltAllEmployee: false,
        sltWeekOff: [],
        from_date: getInitialDate(roster_from_date),
        to_date: getInitialDate(roster_to_date),
        isPublishClked: false,
        totalEmployee: 0
    });

    const [dates, setDates] = useState({
        fromDate: getInitialDate(roster_from_date),
        toDate: getInitialDate(roster_to_date)
    });

    const [dropDownData, setDropdownData] = useState({
        shiftList: [],
        departmentList: [],
        employeeList: [],
    });

    const [loading, setLoading] = useState(false);
    const [showWeekDates, setShowWeekDates] = useState([]);
    const [currentWeekPage, setCurrentWeekPage] = useState(0);

    // Use refs to track current values to avoid stale state
    const currentShiftRef = useRef(formData.shift);
    const currentWeekOffRef = useRef(formData.sltWeekOff);
    const hasSelectedEmployeesRef = useRef(false);

    // Update refs when formData changes
    useEffect(() => {
        currentShiftRef.current = formData.shift;
        currentWeekOffRef.current = formData.sltWeekOff;
        hasSelectedEmployeesRef.current = formData.rotational_shifts.some(
            shift => shift.employee.is_emp_selected
        );
    }, [formData.shift, formData.sltWeekOff, formData.rotational_shifts]);

    // Check if any employees are selected
    const hasSelectedEmployees = formData.rotational_shifts.some(
        shift => shift.employee.is_emp_selected
    );

    // Calculate week dates based on current page
    useEffect(() => {
        if (dates.fromDate && dates.toDate) {
            const startDate = dayjs(dates.fromDate).add(currentWeekPage * 7, 'day');
            const endDate = dayjs(startDate).add(6, 'day'); // Show 7 days per page

            const datesInWeek = [];
            let currentDate = startDate;

            for (let i = 0; i < 7; i++) {
                if (currentDate.isAfter(dates.toDate)) break;
                datesInWeek.push(currentDate);
                currentDate = currentDate.add(1, 'day');
            }

            setShowWeekDates(datesInWeek);
        }
    }, [dates.fromDate, dates.toDate, currentWeekPage]);

    // Transform API data based on grouping type
    const transformRotationalShifts = (apiData, groupBy) => {
        if (!apiData || !apiData.rotational_shifts) return [];

        if (groupBy === "employee") {
            // For employee grouping, data is already in the expected format
            return apiData.rotational_shifts.map(item => {
                // Check if employee has any assigned shifts
                const hasAssignedShifts = item.shift?.some(shift =>
                    shift.shift?.is_assigned === true
                );

                return {
                    ...item,
                    employee: {
                        ...item.employee,
                        is_emp_selected: hasAssignedShifts // Auto-select if has assigned shifts
                    }
                };
            });
        } else if (groupBy === "department" || groupBy === "team") {
            // For department/team grouping, flatten the structure
            const flattened = [];
            apiData.rotational_shifts.forEach(group => {
                const groupKey = groupBy === "department" ? "department" : "team";
                const groupData = group[groupKey];

                if (group.employees && group.employees.length > 0) {
                    group.employees.forEach(employeeData => {
                        // Check if employee has any assigned shifts
                        const hasAssignedShifts = employeeData.shift?.some(shift =>
                            shift.shift?.is_assigned === true
                        );

                        flattened.push({
                            employee: {
                                ...employeeData.employee,
                                is_emp_selected: hasAssignedShifts, // Auto-select if has assigned shifts
                                // Add group information for display
                                [groupKey]: groupData
                            },
                            shift: employeeData.shift
                        });
                    });
                }
            });
            return flattened;
        }
        return [];
    };

    // Calculate total employees from rotational_shifts
    const calculateTotalEmployees = (rotationalShifts, groupBy) => {
        if (groupBy === "employee") {
            return rotationalShifts.length;
        } else if (groupBy === "department" || groupBy === "team") {
            let total = 0;
            rotationalShifts.forEach(group => {
                total += (group.employees?.length || 0);
            });
            return total;
        }
        return 0;
    };

    // Group employees by department/team for hierarchical view
    const getGroupedEmployees = () => {
        if (formData.groupBy === "employee") {
            return null; // No grouping needed for employee view
        }

        const groupedData = {};
        const groupKey = formData.groupBy === "department" ? "department" : "team";

        formData.rotational_shifts.forEach(employeeShift => {
            const group = employeeShift.employee[groupKey];

            if (group) {
                const groupId = group.id;
                if (!groupedData[groupId]) {
                    groupedData[groupId] = {
                        group: group,
                        employees: [],
                        is_emp_selected: false
                    };
                }
                groupedData[groupId].employees.push(employeeShift);
            }
        });

        return groupedData;
    };

    // Handle group selection (select/deselect all employees in a group)
    const handleGroupSelectChange = (groupId, checked) => {
        const groupedData = getGroupedEmployees();
        if (!groupedData || !groupedData[groupId]) return;

        const employeeIds = groupedData[groupId].employees.map(emp => emp.employee.id);

        setFormData(prev => ({
            ...prev,
            rotational_shifts: prev.rotational_shifts.map(employeeShift =>
                employeeIds.includes(employeeShift.employee.id)
                    ? {
                        ...employeeShift,
                        employee: {
                            ...employeeShift.employee,
                            is_emp_selected: checked
                        }
                    }
                    : employeeShift
            )
        }));
    };

    // Check if all employees in a group are selected
    const isGroupSelected = (groupId) => {
        const groupedData = getGroupedEmployees();
        if (!groupedData || !groupedData[groupId]) return false;

        return groupedData[groupId].employees.every(emp => emp.employee.is_emp_selected);
    };

    // Check if some employees in a group are selected (for indeterminate state)
    const isGroupIndeterminate = (groupId) => {
        const groupedData = getGroupedEmployees();
        if (!groupedData || !groupedData[groupId]) return false;

        const employees = groupedData[groupId].employees;
        const selectedCount = employees.filter(emp => emp.employee.is_emp_selected).length;
        return selectedCount > 0 && selectedCount < employees.length;
    };

    // Fetch dropdown data
    const getShiftList = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_SHIFT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getDepartmentList = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DEPARTMENT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getEmployeeList = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const fetchDropdownData = async () => {
        try {
            setLoading(true);
            const [shiftList, departmentList, employeeList] = await Promise.all([
                getShiftList(),
                getDepartmentList(),
                getEmployeeList()
            ]);

            setDropdownData({
                shiftList: shiftList || [],
                departmentList: departmentList || [],
                employeeList: employeeList || [],
            });
        } catch (error) {
            ToastMsg('error', 'Failed to load dropdown data');
        } finally {
            setLoading(false);
        }
    };

    const getIndividualRecord = useCallback(async (params = {}) => {
        try {
            setLoading(true);

            // Only make API call if both from_date and to_date are provided
            if (!params.from_date || !params.to_date) {
                return;
            }

            const apiData = await fetch(INDIVIDUAL_ROSTERING_GET, params);

            if (apiData?.status === "success" && apiData.data) {
                const transformedShifts = transformRotationalShifts(apiData.data, params.group_by);
                const totalEmployees = calculateTotalEmployees(apiData.data.rotational_shifts, params.group_by);

                // Update formData with the API response
                setFormData(prev => ({
                    ...prev,
                    rotational_shifts: transformedShifts,
                    initial_rotational_shifts: transformedShifts,
                    totalEmployee: totalEmployees,
                    is_published: apiData.data.is_published || false,
                    sltWeekOff: apiData.data.common_week_off || []
                }));

            } else {
                ToastMsg('error', apiData?.message || 'Failed to load roster data');
            }
        } catch (error) {
            ToastMsg('error', error.message || 'Failed to load roster data');
        } finally {
            setLoading(false);
        }
    }, [fetch]);

    // Handle date changes with validation
    const handleDateChange = (field, value) => {
        // Validate date range
        if (field === 'fromDate' && value && dates.toDate && dayjs(value).isAfter(dates.toDate)) {
            ToastMsg('warning', 'From date cannot be after To date');
            return;
        }
        if (field === 'toDate' && value && dates.fromDate && dayjs(value).isBefore(dates.fromDate)) {
            ToastMsg('warning', 'To date cannot be before From date');
            return;
        }

        setDates(prev => ({
            ...prev,
            [field]: value
        }));

        setFormData(prev => ({
            ...prev,
            [field === 'fromDate' ? 'from_date' : 'to_date']: value
        }));

        // Reset to first week when dates change
        setCurrentWeekPage(0);
    };

    // Effect to fetch individual record when dates or groupBy changes
    useEffect(() => {
        const fetchRosterData = async () => {
            const params = {
                roster_id: roster_id || '',
                group_by: formData.groupBy,
                from_date: dates.fromDate ? dayjs(dates.fromDate).format('YYYY-MM-DD') : '',
                to_date: dates.toDate ? dayjs(dates.toDate).format('YYYY-MM-DD') : ''
            };

            // Only fetch if both dates are available
            if (dates.fromDate && dates.toDate) {
                await getIndividualRecord(params);
            }
        };

        fetchRosterData();
    }, [dates.fromDate, dates.toDate, roster_id, formData.groupBy]);

    // Initial data fetch on component mount
    useEffect(() => {
        fetchDropdownData();
    }, []);

    const handleCancel = () => {
        navigate(-1);
    };

    const convertStructure = (rotationalArr) => {
        if (!rotationalArr?.length) {
            console.log('rotationalArr is empty or undefined');
            return [];
        }

        const processEmployee = (empData) => {
            const updatedShift = empData?.shift?.map(shiftData => ({
                date: shiftData?.date,
                shift: shiftData?.shift?.id || "",
                is_leave: shiftData?.is_leave || false,
                is_weekoff: shiftData?.is_weekoff || false
            }));
            return { employee: empData?.employee?.id, shift: updatedShift };
        };

        const filterAndProcessEmployees = (arr) => {
            if (!arr?.length) {
                console.log('Array is empty in filterAndProcessEmployees');
                return [];
            }

            const filtered = arr.reduce((result, item) => {
                console.log('Checking item:', item);
                console.log('is_emp_selected:', item?.employee?.is_emp_selected);

                if (item?.employee?.is_emp_selected) {
                    result.push(processEmployee(item));
                }
                return result;
            }, []);

            return filtered;
        };

        if (formData.groupBy === "employee") {
            const result = filterAndProcessEmployees(rotationalArr);
            console.log('Employee grouping result:', result);
            return result;
        } else {
            const result = rotationalArr.reduce((result, departmentData) => {
                if (departmentData?.employee) {

                    // Check if this single employee is selected
                    if (departmentData.employee?.is_emp_selected) {
                        const processedEmployee = processEmployee(departmentData);
                        result.push(processedEmployee);
                    }
                }
                // Also check if there's an employees array (for multiple employees in a department)
                else if (departmentData?.employees?.length) {
                    const processedEmployees = filterAndProcessEmployees(departmentData.employees);
                    result.push(...processedEmployees);
                }

                return result;
            }, []);
            return result;
        }
    };

    const handleSave = async () => {
        try {
            let { fromDate, toDate } = dates;

            const payload = {
                roster_id: roster_id || undefined,
                from_date: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : "",
                to_date: toDate ? dayjs(toDate).format('YYYY-MM-DD') : "",
                is_published: formData.is_published || false,
                rotational_shifts: convertStructure(formData.rotational_shifts),
                common_week_off: formData.common_week_off || [],
                remarks: formData.remarks || undefined
            };

            // console.log('Transformed Payload for Backend:', payload);

            // Validation check for empty rotational_shifts
            if (!payload.rotational_shifts || payload.rotational_shifts.length === 0) {
                ToastMsg("error", "Kindly select employees to update shifts.");
                return;
            }

            // Use your custom update method
            const data = await update(INDIVIDUAL_ROSTERING_GET, payload);

            if (data.status === 'success') {
                // Reset form data
                setFormData({
                    groupBy: "employee",
                    shift: undefined,
                    assignedEmp: false,
                    isMakeRecurring: false,
                    rotational_shifts: [],
                    initial_rotational_shifts: [],
                    is_published: false,
                    sltAllEmployee: false,
                    from_date: null,
                    to_date: null,
                    department: undefined,
                    employee: undefined,
                    sltWeekOff: [],
                    isPublishClked: false,
                    remarks: undefined
                });

                setDates({
                    fromDate: null,
                    toDate: null
                });

                navigate('/roster');
                ToastMsg('success', data.message);
            } else {
                ToastMsg('error', data.message || 'Failed to save roster');
            }

        } catch (error) {
            console.error('Error saving roster:', error);
            ToastMsg('error', 'Failed to save roster');
        }
    };

    const handlePublish = async () => {
        try {
            let { fromDate, toDate } = dates;

            const payload = {
                roster_id: roster_id || undefined,
                from_date: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : "",
                to_date: toDate ? dayjs(toDate).format('YYYY-MM-DD') : "",
                is_published: true,
                rotational_shifts: convertStructure(formData.rotational_shifts),
                common_week_off: formData.common_week_off || [],
                remarks: formData.remarks || undefined
            };

            // console.log('Transformed Payload for Backend:', payload);

            // Validation check for empty rotational_shifts
            if (!payload.rotational_shifts || payload.rotational_shifts.length === 0) {
                ToastMsg("error", "Kindly select employees to update shifts.");
                return;
            }

            // Use your custom update method
            const data = await update(INDIVIDUAL_ROSTERING_GET, payload);

            if (data.status === 'success') {
                // Reset form data
                setFormData({
                    groupBy: "employee",
                    shift: undefined,
                    assignedEmp: false,
                    isMakeRecurring: false,
                    rotational_shifts: [],
                    initial_rotational_shifts: [],
                    is_published: false,
                    sltAllEmployee: false,
                    from_date: null,
                    to_date: null,
                    department: undefined,
                    employee: undefined,
                    sltWeekOff: [],
                    isPublishClked: false,
                    remarks: undefined
                });

                setDates({
                    fromDate: null,
                    toDate: null
                });

                navigate('/roster');
                ToastMsg('success', data.message);
            } else {
                ToastMsg('error', data.message || 'Failed to save roster');
            }

        } catch (error) {
            console.error('Error saving roster:', error);
            ToastMsg('error', 'Failed to save roster');
        }
    };

    const handleGroupByChange = (group) => {
        setFormData(prev => ({ ...prev, groupBy: group }));
    };

    // Week Navigation Handlers
    const handlePrevWeek = () => {
        if (currentWeekPage > 0) {
            setCurrentWeekPage(prev => prev - 1);
        }
    };

    const handleNextWeek = () => {
        const nextWeekStart = dayjs(dates.fromDate).add((currentWeekPage + 1) * 7, 'day');
        if (nextWeekStart.isBefore(dates.toDate) || nextWeekStart.isSame(dates.toDate)) {
            setCurrentWeekPage(prev => prev + 1);
        }
    };

    // Select All Employees Handler
    const handleSelectAllChange = (checked) => {
        setFormData(prev => ({
            ...prev,
            sltAllEmployee: checked,
            rotational_shifts: prev.rotational_shifts.map(employeeShift => ({
                ...employeeShift,
                employee: {
                    ...employeeShift.employee,
                    is_emp_selected: checked
                }
            }))
        }));
    };

    // Individual Employee Selection Handler
    const handleEmployeeSelectChange = (employeeId, checked) => {
        setFormData(prev => ({
            ...prev,
            rotational_shifts: prev.rotational_shifts.map(employeeShift =>
                employeeShift.employee.id === employeeId
                    ? {
                        ...employeeShift,
                        employee: {
                            ...employeeShift.employee,
                            is_emp_selected: checked
                        }
                    }
                    : employeeShift
            )
        }));
    };

    // Apply selected shift to all days for selected employees (respecting week off days)
    const applyShiftToSelectedEmployees = (shiftValue = null) => {
        // Use the provided shiftValue or fall back to current formData.shift
        const shiftToApply = shiftValue !== null ? shiftValue : currentShiftRef.current;

        if (!shiftToApply || !hasSelectedEmployeesRef.current) {
            ToastMsg('warning', 'Please select both employees and a shift first');
            return;
        }

        const selectedShiftDetails = dropDownData.shiftList.find(s => s.id === shiftToApply);
        if (!selectedShiftDetails) {
            ToastMsg('error', 'Selected shift not found');
            return;
        }

        const updatedShifts = formData.rotational_shifts.map(employeeShift => {
            if (employeeShift.employee.is_emp_selected) {
                // Generate shifts for all dates in the range
                const updatedShift = showWeekDates.map(date => {
                    const existingShift = employeeShift.shift?.find(shift =>
                        dayjs(shift.date).isSame(date, 'day')
                    );

                    // Get the day name in lowercase for comparison
                    const dayName = date.format('dddd').toLowerCase();
                    const isConfiguredWeekOff = currentWeekOffRef.current.includes(dayName);

                    // If it's a configured week off day, always mark as week off
                    if (isConfiguredWeekOff) {
                        return {
                            date: date.format('YYYY-MM-DD'),
                            shift: null,
                            is_weekoff: true,
                            is_leave: existingShift?.is_leave || false
                        };
                    }

                    // For non-week-off days, apply the selected shift
                    // But preserve existing data if it's a manual assignment
                    if (existingShift) {
                        // If there's existing data and it's not a configured week off, update the shift
                        return {
                            ...existingShift,
                            shift: {
                                id: shiftToApply,
                                name: selectedShiftDetails.shift_name,
                                is_assigned: true
                            },
                            is_weekoff: existingShift.is_weekoff, // Preserve existing week off status
                            is_leave: existingShift.is_leave // Preserve existing leave status
                        };
                    }

                    // No existing shift data, create new shift assignment
                    return {
                        date: date.format('YYYY-MM-DD'),
                        shift: {
                            id: shiftToApply,
                            name: selectedShiftDetails.shift_name,
                            is_assigned: true
                        },
                        is_weekoff: false,
                        is_leave: false
                    };
                });

                return {
                    ...employeeShift,
                    shift: updatedShift
                };
            }
            return employeeShift;
        });

        setFormData(prev => ({
            ...prev,
            rotational_shifts: updatedShifts
        }));

        ToastMsg('success', `${selectedShiftDetails.shift_name} shift applied to all selected employees`);
    };

    // Handle shift dropdown change - auto-apply to selected employees
    const handleShiftChange = (value) => {
        setFormData(prev => ({ ...prev, shift: value }));

        // Auto-apply the shift to selected employees when shift is changed
        if (value && hasSelectedEmployeesRef.current) {
            // Apply immediately using the value parameter to avoid stale state
            applyShiftToSelectedEmployees(value);
        }
    };

    // Handle week off dropdown change
    const handleWeekOffChange = (value) => {
        setFormData(prev => ({ ...prev, sltWeekOff: value }));

        // If shift is selected and employees are selected, re-apply the shift to respect new week off days
        if (currentShiftRef.current && hasSelectedEmployeesRef.current) {
            // Use setTimeout to ensure state is updated before applying shifts
            const timeoutId = setTimeout(() => {
                applyShiftToSelectedEmployees();
            }, 100);

            // Cleanup timeout on unmount
            return () => clearTimeout(timeoutId);
        }
    };

    // Shift Click Handler - for direct day clicks
    const handleShiftClick = (employeeId, date, currentShift) => {
        // if (!editAccess) return;

        // Check if the clicked day is a configured week off
        const dayName = date.format('dddd').toLowerCase();
        const isConfiguredWeekOff = formData.sltWeekOff.includes(dayName);

        // If shift is selected and we're clicking on a specific day, apply that shift to just that day
        if (formData.shift && !isConfiguredWeekOff) {
            const selectedShiftDetails = dropDownData.shiftList.find(s => s.id === formData.shift);

            const updatedShifts = formData.rotational_shifts.map(employeeShift => {
                if (employeeShift.employee.id === employeeId) {
                    // Check if shift already exists for this date
                    const existingShiftIndex = employeeShift.shift.findIndex(shift =>
                        dayjs(shift.date).isSame(date, 'day')
                    );

                    let updatedShift;
                    if (existingShiftIndex >= 0) {
                        // Update existing shift
                        updatedShift = [...employeeShift.shift];
                        updatedShift[existingShiftIndex] = {
                            ...updatedShift[existingShiftIndex],
                            shift: {
                                id: formData.shift,
                                name: selectedShiftDetails?.shift_name,
                                is_assigned: true
                            },
                            is_weekoff: false,
                            is_leave: false
                        };
                    } else {
                        // Add new shift
                        updatedShift = [
                            ...employeeShift.shift,
                            {
                                date: date.format('YYYY-MM-DD'),
                                shift: {
                                    id: formData.shift,
                                    name: selectedShiftDetails?.shift_name,
                                    is_assigned: true
                                },
                                is_weekoff: false,
                                is_leave: false
                            }
                        ];
                    }

                    return {
                        ...employeeShift,
                        shift: updatedShift
                    };
                }
                return employeeShift;
            });

            setFormData(prev => ({
                ...prev,
                rotational_shifts: updatedShifts
            }));

            ToastMsg('success', `${selectedShiftDetails?.shift_name} shift assigned to ${date.format('DD/MM/YYYY')}`);
        } else if (!formData.shift || isConfiguredWeekOff) {
            // If no shift is selected OR it's a configured week off day, toggle week off status
            // But don't allow toggling configured week off days
            if (isConfiguredWeekOff) {
                ToastMsg('warning', 'Cannot modify configured week off days. Please update the week off selection above.');
                return;
            }

            const updatedShifts = formData.rotational_shifts.map(employeeShift => {
                if (employeeShift.employee.id === employeeId) {
                    const existingShiftIndex = employeeShift.shift.findIndex(shift =>
                        dayjs(shift.date).isSame(date, 'day')
                    );

                    let updatedShift;
                    if (existingShiftIndex >= 0) {
                        // Toggle week off status
                        updatedShift = [...employeeShift.shift];
                        updatedShift[existingShiftIndex] = {
                            ...updatedShift[existingShiftIndex],
                            is_weekoff: !updatedShift[existingShiftIndex].is_weekoff,
                            is_leave: false,
                            shift: updatedShift[existingShiftIndex].is_weekoff ?
                                (updatedShift[existingShiftIndex].shift || null) : null
                        };
                    } else {
                        // Create new week off entry
                        updatedShift = [
                            ...employeeShift.shift,
                            {
                                date: date.format('YYYY-MM-DD'),
                                shift: null,
                                is_weekoff: true,
                                is_leave: false
                            }
                        ];
                    }

                    return {
                        ...employeeShift,
                        shift: updatedShift
                    };
                }
                return employeeShift;
            });

            setFormData(prev => ({
                ...prev,
                rotational_shifts: updatedShifts
            }));

            const newWeekOffStatus = currentShift?.is_weekoff ? 'removed from' : 'marked as';
            ToastMsg('success', `Day ${newWeekOffStatus} week off for ${date.format('DD/MM/YYYY')}`);
        }
    };

    // Shift Cell Component
    const ShiftCell = ({ shift, date, employeeId, onShiftClick, editAccess }) => {
        const isWeekOffDay = formData.sltWeekOff.includes(date.format('dddd').toLowerCase());
        const isConfiguredWeekOff = !shift && isWeekOffDay;
        const isManualWeekOff = shift?.is_weekoff && !isWeekOffDay;

        const getShiftDisplay = () => {
            if (isConfiguredWeekOff) return 'Week Off';
            if (isManualWeekOff) return 'Week Off';
            if (!shift) return 'Not Assigned';
            if (shift.is_weekoff) return 'Week Off';
            if (shift.is_leave) return 'Leave';
            return shift.shift?.name || 'Not Assigned';
        };

        const getBackgroundColor = () => {
            if (isConfiguredWeekOff || isManualWeekOff) return 'bg-red-100 dark:bg-red-900/30';
            if (!shift) return 'bg-gray-100 dark:bg-gray-600';
            if (shift.is_weekoff) return 'bg-red-100 dark:bg-red-900/30';
            if (shift.is_leave) return 'bg-orange-100 dark:bg-orange-900/30';
            if (shift.shift?.is_assigned) return 'bg-green-100 dark:bg-green-900/30';
            return 'bg-gray-100 dark:bg-gray-600';
        };

        const getTextColor = () => {
            if (isConfiguredWeekOff || isManualWeekOff) return 'text-red-700 dark:text-red-300';
            if (!shift) return 'text-gray-600 dark:text-gray-400';
            if (shift.is_weekoff) return 'text-red-700 dark:text-red-300';
            if (shift.is_leave) return 'text-orange-700 dark:text-orange-300';
            if (shift.shift?.is_assigned) return 'text-green-700 dark:text-green-300';
            return 'text-gray-600 dark:text-gray-400';
        };

        return (
            <div
                className={`p-2 rounded text-xs font-medium transition-all duration-200 hover:shadow-md ${getBackgroundColor()} ${getTextColor()} border border-gray-300 dark:border-gray-600 
                `}
                onClick={() => onShiftClick(employeeId, date, shift)}
                title={getShiftDisplay()}
            >
                <div className="truncate text-center">{getShiftDisplay()}</div>
            </div>
        );
    };

    // Check if all employees are selected
    const allEmployeesSelected = formData.rotational_shifts.length > 0 &&
        formData.rotational_shifts.every(shift => shift.employee.is_emp_selected);

    // Render table body based on grouping type
    const renderTableBody = () => {
        if (formData.groupBy === "employee") {
            // Flat employee view
            return formData.rotational_shifts.map((employeeShift, index) => (
                <tr
                    key={employeeShift.employee.id}
                    className={`${index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'} hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200`}
                >
                    <td className="w-80 px-4 py-3 border-r border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-3">
                            <Checkbox
                                checked={employeeShift.employee.is_emp_selected || false}
                                onChange={(e) => handleEmployeeSelectChange(employeeShift.employee.id, e.target.checked)}
                            />
                            <span className="text-xs">
                                <EmployeeDetails details={{
                                    ...employeeShift?.employee,
                                    "title": employeeShift?.employee?.title || "",
                                    "profile": employeeShift?.employee?.profile || "",
                                    "designation_id": employeeShift?.employee?.designation
                                        ? { id: employeeShift?.employee?.designation.id, designation_name: employeeShift?.employee?.designation.name }
                                        : { id: 0, designation_name: "" },
                                    "first_name": employeeShift?.employee?.employee_name || "",
                                    "id": employeeShift?.employee?.employee_id || ""
                                }} size={'xs'} profileWrap={true} />
                            </span>
                        </div>
                    </td>

                    {showWeekDates.map((date, dateIndex) => {
                        const shiftForDate = employeeShift.shift?.find(shift =>
                            dayjs(shift.date).isSame(date, 'day')
                        );

                        return (
                            <td
                                key={date.format('YYYY-MM-DD')}
                                className={`min-w-[120px] px-3 py-3 text-center ${dateIndex < showWeekDates.length - 1
                                    ? 'border-r border-gray-200 dark:border-gray-600'
                                    : ''}`}
                            >
                                <ShiftCell
                                    shift={shiftForDate}
                                    date={date}
                                    employeeId={employeeShift.employee.id}
                                    onShiftClick={handleShiftClick}
                                    editAccess={editAccess}
                                />
                            </td>
                        );
                    })}
                </tr>
            ));
        } else {
            // Hierarchical department/team view - SINGLE tbody
            const groupedData = getGroupedEmployees();
            if (!groupedData) return null;

            return (
                <>
                    {Object.values(groupedData).map((groupData, groupIndex) => (
                        <React.Fragment key={`group-${groupData.group.id}-${groupIndex}`}>
                            {/* Group Header Row */}
                            <tr className="bg-gray-100 dark:bg-gray-700 border-t-2 border-gray-200 dark:border-gray-600">
                                <td className="w-80 px-4 py-3 border-r border-gray-200 dark:border-gray-600">
                                    <div className="flex items-center gap-3">
                                        <Checkbox
                                            checked={isGroupSelected(groupData.group.id)}
                                            indeterminate={isGroupIndeterminate(groupData.group.id)}
                                            onChange={(e) => handleGroupSelectChange(groupData.group.id, e.target.checked)}
                                        />
                                        <div className="flex flex-col">
                                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                                {groupData.group.name}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {groupData.employees.length} employee{groupData.employees.length !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </div>
                                </td>

                                {/* Group header cells spanning all dates */}
                                {showWeekDates.map((date, index) => (
                                    <td
                                        key={date.format('YYYY-MM-DD')}
                                        className={`min-w-[120px] px-3 py-3 text-center ${index < showWeekDates.length - 1
                                            ? 'border-r border-gray-200 dark:border-gray-600'
                                            : ''}`}
                                    >
                                        {/* You can show group-level info here if needed */}
                                    </td>
                                ))}
                            </tr>

                            {/* Employee Rows for this Group */}
                            {groupData.employees.map((employeeShift, employeeIndex) => (
                                <tr
                                    key={employeeShift.employee.id}
                                    className={`${employeeIndex % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'} hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200`}
                                >
                                    <td className="w-80 px-4 py-3 border-r border-gray-200 dark:border-gray-600">
                                        <div className="flex items-center gap-3 pl-8">
                                            <Checkbox
                                                checked={employeeShift.employee.is_emp_selected || false}
                                                onChange={(e) => handleEmployeeSelectChange(employeeShift.employee.id, e.target.checked)}
                                            />
                                            <span className="text-xs">
                                                <EmployeeDetails details={{
                                                    ...employeeShift?.employee,
                                                    "title": employeeShift?.employee?.title || "",
                                                    "profile": employeeShift?.employee?.profile || "",
                                                    "designation_id": employeeShift?.employee?.designation
                                                        ? { id: employeeShift?.employee?.designation.id, designation_name: employeeShift?.employee?.designation.name }
                                                        : { id: 0, designation_name: "" },
                                                    "first_name": employeeShift?.employee?.employee_name || "",
                                                    "id": employeeShift?.employee?.employee_id || ""
                                                }} size={'xs'} profileWrap={true} />
                                            </span>
                                        </div>
                                    </td>

                                    {showWeekDates.map((date, dateIndex) => {
                                        const shiftForDate = employeeShift.shift?.find(shift =>
                                            dayjs(shift.date).isSame(date, 'day')
                                        );

                                        return (
                                            <td
                                                key={date.format('YYYY-MM-DD')}
                                                className={`min-w-[120px] px-3 py-3 text-center ${dateIndex < showWeekDates.length - 1
                                                    ? 'border-r border-gray-200 dark:border-gray-600'
                                                    : ''}`}
                                            >
                                                <ShiftCell
                                                    shift={shiftForDate}
                                                    date={date}
                                                    employeeId={employeeShift.employee.id}
                                                    onShiftClick={handleShiftClick}
                                                    editAccess={editAccess}
                                                />
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </>
            );
        }
    };

    return (
        <ContainerCard className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <div
                    className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 
                               dark:bg-gray-700 dark:hover:bg-gray-600 cursor-pointer transition-all duration-200 
                               group shadow-sm"
                    onClick={handleCancel}
                >
                    <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-300 group-hover:text-gray-900 
                                       dark:group-hover:text-white transition-colors" />
                </div>

                <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                        {editAccess ? "Edit Shift Rostering" : "Add Shift Rostering"}
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 font-medium">
                        Configure working hours and breaks for employees
                    </p>
                </div>

                <div className="flex justify-end items-center gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-lg border border-blue-100 
                              dark:border-blue-800">
                        <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                            Total Employees: <span className="text-blue-900 dark:text-blue-100">{formData.totalEmployee}</span>
                        </p>
                    </div>

                    <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                        <Button
                            variant='ghost'
                            onClick={() => handleGroupByChange('employee')}
                            className={`${formData.groupBy === 'employee'
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                } hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 
                              font-medium text-sm px-4 py-2 h-auto`}
                        >
                            Employee
                        </Button>
                        <Button
                            variant='ghost'
                            onClick={() => handleGroupByChange('department')}
                            className={`${formData.groupBy === 'department'
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                } hover:bg-white dark:hover:bg-gray-600 transition-all duration-200 
                              font-medium text-sm px-4 py-2 h-auto`}
                        >
                            Department
                        </Button>
                        <Button
                            variant='ghost'
                            onClick={() => handleGroupByChange('team')}
                            className={`${formData.groupBy === 'team'
                                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                } hover:bg-white dark:hover:bg-gray-600 transition-all duration-200 
                              font-medium text-sm px-4 py-2 h-auto`}
                        >
                            Team
                        </Button>
                    </div>
                </div>
            </div>

            {/* Second Section - Roster Configuration */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 
                shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
                <div className="flex flex-col gap-6">

                    {/* Form Controls */}
                    <div className="flex flex-col xl:flex-row gap-6">

                        {/* Date Pickers Group */}
                        <div className="flex flex-col sm:flex-row gap-4 flex-1 flex-wrap">
                            <div className="space-y-3 flex-1 min-w-[180px]">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select From Date <span className="text-red-500 ml-1">*</span>
                                </label>
                                <DatePicker
                                    value={dates.fromDate}
                                    onChange={(date) => handleDateChange('fromDate', date)}
                                    format="DD-MM-YYYY"
                                    disabledDate={disabledPastDate}
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    )}
                                    size="large"
                                />
                            </div>

                            <div className="space-y-3 flex-1 min-w-[180px]">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select To Date <span className="text-red-500 ml-1">*</span>
                                </label>
                                <DatePicker
                                    value={dates.toDate}
                                    onChange={(date) => handleDateChange('toDate', date)}
                                    format="DD-MM-YYYY"
                                    disabledDate={disabledPastDate}
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    )}
                                    size="large"
                                />
                            </div>
                        </div>

                        {/* Dropdowns Group */}
                        <div className="flex flex-col sm:flex-row gap-4 flex-1 flex-wrap">
                            <div className="space-y-3 flex-1 min-w-[180px]">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select Shift <span className="text-red-500 ml-1">*</span>
                                </label>
                                <Select
                                    placeholder="Select shift"
                                    value={formData.shift}
                                    onChange={handleShiftChange}
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    )}
                                    size="large"
                                    options={dropDownData.shiftList.map(shift => ({
                                        value: shift.id,
                                        label: shift.shift_name
                                    }))}
                                    disabled={!hasSelectedEmployees || !formData.from_date || !formData.to_date}
                                />
                            </div>

                            <div className="space-y-3 flex-1 min-w-[180px]">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select WeekOff Day <span className="text-red-500 ml-1">*</span>
                                </label>
                                <Select
                                    mode="multiple"
                                    placeholder="Select Week off"
                                    value={formData.sltWeekOff}
                                    onChange={handleWeekOffChange}
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    )}
                                    size="large"
                                    disabled={!hasSelectedEmployees || !formData.from_date || !formData.to_date}
                                    options={[
                                        { value: 'monday', label: 'Monday' },
                                        { value: 'tuesday', label: 'Tuesday' },
                                        { value: 'wednesday', label: 'Wednesday' },
                                        { value: 'thursday', label: 'Thursday' },
                                        { value: 'friday', label: 'Friday' },
                                        { value: 'saturday', label: 'Saturday' },
                                        { value: 'sunday', label: 'Sunday' }
                                    ]}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Action & Legend Section */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between w-full gap-4 mt-6">

                        {/* Legend Section */}
                        <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm w-full lg:w-auto">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                <span className="text-sm text-gray-700 dark:text-gray-300">Week Off</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                                <span className="text-sm text-gray-700 dark:text-gray-300">Leave</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                <span className="text-sm text-gray-700 dark:text-gray-300">Assigned Shift</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                                <span className="text-sm text-gray-700 dark:text-gray-300">Not Assigned</span>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        {
                            !formData.is_published && (
                                <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto justify-end">
                                    <Button
                                        variant="brand"
                                        onClick={handleSave}
                                        className={cn(
                                            "transition-colors duration-200",
                                            "flex items-center justify-center gap-2 px-6 py-2 rounded-lg w-full sm:w-auto"
                                        )}
                                        disabled={
                                            // !editAccess ||
                                            !hasSelectedEmployees
                                        }
                                    >
                                        <Save className="w-4 h-4" />
                                        Save
                                    </Button>
                                    <Button
                                        onClick={handlePublish}
                                        className={cn(
                                            "bg-green-500 hover:bg-green-600",
                                            "dark:bg-green-500 dark:hover:bg-green-600",
                                            "text-white transition-colors duration-200",
                                            "flex items-center justify-center gap-2 px-6 py-2 rounded-lg w-full sm:w-auto"
                                        )}
                                        disabled={
                                            // !editAccess ||
                                            !hasSelectedEmployees
                                        }
                                    >
                                        <Send className="w-4 h-4" />
                                        Publish
                                    </Button>
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>

            {/* Third Section - Employee Roster Display */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg p-6 mt-6 transition-all duration-300 hover:shadow-xl">

                {/* Week Navigation Header */}
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
                    <div className="flex justify-start items-center gap-3">
                        <Button
                            variant="ghost"
                            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                            onClick={handlePrevWeek}
                            disabled={currentWeekPage === 0}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Previous Week
                        </Button>
                    </div>

                    {/* Week Display */}
                    <div className="text-center">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {showWeekDates.length > 0
                                ? `${showWeekDates[0]?.format('DD MMM YYYY')} - ${showWeekDates[showWeekDates.length - 1]?.format('DD MMM YYYY')}`
                                : 'Select dates to view roster'
                            }
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Week {currentWeekPage + 1}
                        </p>
                    </div>

                    <Button
                        variant="ghost"
                        className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                        onClick={handleNextWeek}
                        disabled={!dates.toDate || dayjs(dates.fromDate).add((currentWeekPage + 1) * 7, 'day').isAfter(dates.toDate)}
                        title='Next Week'
                    >
                        Next Week
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>

                {/* Roster Table */}
                <div className="relative overflow-x-auto overflow-y-auto max-h-[600px] rounded-lg border border-gray-200 dark:border-gray-700">

                    <table className="min-w-full border-collapse">
                        {/* Table Header */}
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-20">
                            <tr>
                                <th className="w-80 px-4 py-3 border-r border-gray-200 dark:border-gray-600 text-left">
                                    <div className="flex items-center gap-3">
                                        <Checkbox
                                            checked={allEmployeesSelected}
                                            onChange={(e) => handleSelectAllChange(e.target.checked)}
                                        />
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                            Employee Details
                                        </h3>
                                    </div>
                                </th>

                                {showWeekDates.length > 0 && showWeekDates.map((date, index) => (
                                    <th
                                        key={date.format('YYYY-MM-DD')}
                                        className={`min-w-[120px] px-3 py-3 text-center font-semibold text-gray-900 dark:text-white ${index < showWeekDates.length - 1
                                            ? 'border-r border-gray-200 dark:border-gray-600'
                                            : ''}`}
                                    >
                                        <div className="text-sm font-medium">{date.format('ddd')}</div>
                                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            {date.format('DD/MM')}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        {/* Table Body */}
                        <tbody className="bg-white dark:bg-gray-800">
                            {formData.rotational_shifts?.length > 0 ? (
                                renderTableBody()
                            ) : (
                                <tr>
                                    <td colSpan={showWeekDates.length + 1} className="text-center py-12 text-gray-500 dark:text-gray-400">
                                        {dates.fromDate && dates.toDate ? 'No employees found for selected dates' : 'Please select dates to view roster'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Loading Overlay */}
            {loading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-xl">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar mx-auto"></div>
                        <p className="mt-2 text-gray-600 dark:text-gray-300">Loading...</p>
                    </div>
                </div>
            )}
        </ContainerCard>
    );
};

export default RosterAddEdit;